import { Box, Grid, Typography, styled } from '@mui/material';
import { Show } from '../../../components/Show';
import { Card } from './Card';
import type { FormSectionElementSchema } from '../../types';
import { useGetShowCondition } from './hooks/useGetShowCondition';
import { useFormRenderers } from '../../context/FormRenderersContext';

type SectionProps = {
  title?: string;
  children?: React.ReactNode;
  gridSize?: number;
  isSubsection?: boolean;
  conditions?: FormSectionElementSchema['conditions'];
  /**
   * Controls for the section as a whole, from `SectionDraftContext`.
   *
   * Absent for every form that installs no adapter, and absent on subsections
   * — see `Element`.
   */
  actions?: React.ReactNode;
  /** Every field in the section's subtree, for a host's renderer (`FormRenderersContext`). */
  fieldIds?: readonly string[];
};

export const Section = (props: SectionProps) => {
  const isShowing = useGetShowCondition(props.conditions);
  const renderers = useFormRenderers();

  if (!isShowing) {
    return null;
  }

  if (renderers?.section) {
    return (
      <>
        {renderers.section({
          title: props.title,
          isSubsection: Boolean(props.isSubsection),
          fieldIds: props.fieldIds ?? [],
          actions: props.actions,
          children: props.children,
        })}
      </>
    );
  }

  if (props.isSubsection) {
    return (
      <Grid item sm={props.gridSize}>
        <Show when={props.title != null}>
          <SubsectionHeading className="contrast-text">
            {props.title}
          </SubsectionHeading>
        </Show>

        <Box
          sx={{ paddingLeft: '24px', marginTop: '8px' }}
          className="subsection-children"
        >
          <Grid container columnSpacing={1} rowSpacing={1}>
            {props.children}
          </Grid>
        </Box>
      </Grid>
    );
  }

  return (
    <Card sx={{ width: '100%' }} className="form-section-wrapper">
      {/* The heading keeps its own bottom rule, so the actions sit on the same
          row rather than under it — the rule is what separates the section from
          its fields, and a control below it would read as the first field. */}
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="space-between"
        gap={1}
        width="100%"
      >
        {/* `width: auto` overrides the heading's own `width: 100%`, which in a
            flex row would claim the whole line and push the actions out. */}
        <SectionHeading sx={{ width: 'auto', flex: 1, minWidth: 0 }}>
          {props.title}
        </SectionHeading>
        {props.actions ? (
          <Box pb="4px" flexShrink={0}>
            {props.actions}
          </Box>
        ) : null}
      </Box>
      <Grid columnSpacing={1} rowSpacing={1} container>
        {props.children}
      </Grid>
    </Card>
  );
};

const SectionHeading = styled(Typography)`
  font-size: 1.6rem;
  text-transform: uppercase;
  color: ${(props) => props.theme.palette.text.secondary};
  border-bottom: 1px solid ${(props) => props.theme.palette.text.secondary};
  width: 100%;
  margin-bottom: 4px;
`;

const SubsectionHeading = styled(Typography)`
  font-size: 1.4rem;
  font-weight: bold;

  .subsection-children & {
    font-size: 1.2rem;
  }
`;
